import {differenceInBusinessDays} from 'date-fns';
import {AlarmClock, CircleCheck, CirclePause, CircleSlash, type LucideIcon} from 'lucide-react';
import {Tooltip, TooltipContent, TooltipTrigger} from '@common-web/components/ui/tooltip';
import {cn} from '@common-web/lib/utils';
import type {RouterOutput} from '@common-web/lib/trpc';

export type PipelineStatus =
  RouterOutput['internal']['groupPipeline']['list']['rows'][number]['status'];
type StatusContext =
  RouterOutput['internal']['groupPipeline']['list']['rows'][number]['statusContext'];

export const STATUS_DISPLAY: Record<
  NonNullable<PipelineStatus>,
  {label: string; icon: LucideIcon; color: string; showWord: boolean}
> = {
  ON_TRACK: {label: 'On track', icon: CircleCheck, color: 'text-green-700', showWord: false},
  OVERDUE: {label: 'Overdue', icon: AlarmClock, color: 'text-amber-700', showWord: true},
  ON_HOLD: {label: 'On hold', icon: CirclePause, color: 'text-violet-700', showWord: true},
  NOT_MOVING_FORWARD: {
    label: 'Not moving forward',
    icon: CircleSlash,
    color: 'text-gray-500',
    showWord: true,
  },
};

const businessDaysLate = (dueAt: Date | string | null | undefined) =>
  dueAt ? Math.max(0, differenceInBusinessDays(new Date(), new Date(dueAt))) : 0;

// Short gray detail after the status word, and the full sentence for the hover.
function describeStatus(status: NonNullable<PipelineStatus>, context: StatusContext | undefined) {
  const step = context?.stepLabel;
  switch (status) {
    case 'OVERDUE': {
      const days = businessDaysLate(context?.dueAt);
      return {
        detail: days > 0 ? `${days}d late` : null,
        sentence: `Overdue${step ? `: ${step}` : ''}${days > 0 ? `, ${days} business ${days === 1 ? 'day' : 'days'} late` : ''}`,
      };
    }
    case 'ON_TRACK':
      return {detail: null, sentence: step ? `On track: ${step}` : 'On track'};
    case 'ON_HOLD':
    case 'NOT_MOVING_FORWARD': {
      const label = STATUS_DISPLAY[status].label;
      const reason = context?.offReason;
      return {
        detail: null,
        sentence: [`${label}${step ? ` at ${step}` : ''}`, reason && `Reason: ${reason}`]
          .filter(Boolean)
          .join('. '),
      };
    }
  }
}

// Icon, word and gray detail; the full explanation is on hover. Groups with no status show nothing.
export function StatusIndicator({
  status,
  context,
}: {
  status: PipelineStatus;
  context?: StatusContext;
}) {
  if (!status) return null;
  const {label, icon: Icon, color, showWord} = STATUS_DISPLAY[status];
  const {detail, sentence} = describeStatus(status, context ?? undefined);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          aria-label={sentence}
          className='inline-flex min-w-0 cursor-help items-center gap-1.5 whitespace-nowrap text-sm'
        >
          <Icon className={cn('h-4 w-4 shrink-0', color)} />
          {showWord && <span className={cn('font-semibold', color)}>{label}</span>}
          {detail && <span className='truncate text-gray-500'>· {detail}</span>}
        </span>
      </TooltipTrigger>
      <TooltipContent className='max-w-xs bg-gray-900 text-white'>{sentence}</TooltipContent>
    </Tooltip>
  );
}
